import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK — Activité & notifications d'un vendeur de marketplace.
//
// Regroupe, pour le tableau de bord vendeur (/vendor-dashboard) :
//   • les commandes reçues — dont celles passées via l'assistant IA ;
//   • les réservations prises sur ses services de réservation ;
//   • les consultations de SES contenus / de SA boutique via le chat IA.
//
// Tout est BEST-EFFORT : une table vide ou une colonne absente ne doit
// jamais casser l'espace vendeur.
// ─────────────────────────────────────────────────────────────

export type VendorNotifKind = 'order' | 'reservation' | 'ai_view';

export interface VendorNotification {
  /** Identifiant unique d'affichage. */
  id: string;
  kind: VendorNotifKind;
  title: string;
  detail: string;
  amount?: number | null;
  currency?: string | null;
  date: string;
  icon: string;
  tone: string;
  /** Commande passée via l'assistant IA. */
  viaAi?: boolean;
}

export interface VendorActivity {
  /** Commandes reçues (toutes). */
  orderCount: number;
  /** Commandes reçues via l'assistant IA. */
  aiOrderCount: number;
  /** Réservations prises sur les services du vendeur. */
  reservationCount: number;
  /** Consultations de la fiche boutique via l'IA. */
  boutiqueViews: number;
  /** Fil d'activité fusionné, du plus récent au plus ancien. */
  notifications: VendorNotification[];
}

export const EMPTY_VENDOR_ACTIVITY: VendorActivity = {
  orderCount: 0,
  aiOrderCount: 0,
  reservationCount: 0,
  boutiqueViews: 0,
  notifications: [],
};

interface OrderRow {
  id: number;
  titre: string | null;
  nomclient: string | null;
  totalttc: string | null;
  monaie: string | null;
  statut_com_vendeur: string | null;
  date_time: string | null;
  noteclient: string | null;
  details: string | null;
}

interface AppointmentRow {
  id: number;
  service_id: number | null;
  fullname: string | null;
  date: string | null;
  time: string | null;
  status: string | null;
  created_at: string | null;
}

interface AiEventRow {
  id: number;
  event_type: string | null;
  product_name: string | null;
  client_name: string | null;
  query: string | null;
  created_at: string | null;
}

const ORDER_VIA_AI_RE = /assistant\s*ia/i;

function isViaAi(note: string | null): boolean {
  return !!note && ORDER_VIA_AI_RE.test(note);
}

function toTime(value: string | null | undefined): number {
  if (!value) return 0;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/** Identifiants des services de réservation appartenant au vendeur. */
async function fetchVendorBookingServiceIds(vendorId: number): Promise<number[]> {
  try {
    const { data } = await supabase
      .from('booking_services')
      .select('id')
      .eq('owner', vendorId);
    return ((data as { id: number }[] | null) || [])
      .map((r) => Number(r.id))
      .filter((n) => Number.isFinite(n));
  } catch {
    return [];
  }
}

/** Nombre de réservations prises sur les services du vendeur. */
export async function fetchVendorReservationCount(vendorId: number | null | undefined): Promise<number> {
  if (!vendorId) return 0;
  try {
    const ids = await fetchVendorBookingServiceIds(vendorId);
    if (ids.length === 0) return 0;
    const { count } = await supabase
      .from('booking_appointments')
      .select('id', { count: 'exact', head: true })
      .in('service_id', ids);
    return count || 0;
  } catch {
    return 0;
  }
}

// ─────────────────────────────────────────────────────────────
// Réservations chiffrées d'un vendeur (pour les statistiques).
// Le montant d'une réservation est déduit du SERVICE réservé : on fait
// correspondre le nom du service de réservation (booking_services) au
// contenu « service » publié par le vendeur (annonce) et l'on reprend son
// prix. Si aucune correspondance n'existe, le montant reste à 0.
// ─────────────────────────────────────────────────────────────

export interface VendorReservationStat {
  id: number;
  /** Nom du service réservé. */
  title: string;
  /** Date brute (création ou date du rendez-vous). */
  date: string | null;
  /** Montant estimé (prix du service correspondant, sinon 0). */
  amount: number;
  status: string | null;
}

export interface VendorReservationStats {
  /** Nombre total de réservations. */
  count: number;
  /** Montant cumulé des réservations. */
  amount: number;
  rows: VendorReservationStat[];
}

export const EMPTY_VENDOR_RESERVATIONS: VendorReservationStats = { count: 0, amount: 0, rows: [] };

function normalizeKey(value: string | null | undefined): string {
  return (value || '').trim().toLowerCase();
}

/**
 * Réservations du vendeur avec leur montant estimé, agrégées pour les stats.
 * Best-effort : jamais bloquant.
 */
export async function fetchVendorReservationStats(
  vendorId: number | null | undefined,
): Promise<VendorReservationStats> {
  if (!vendorId) return EMPTY_VENDOR_RESERVATIONS;
  try {
    // 1) Services de réservation du vendeur (id → nom).
    const { data: svcData } = await supabase
      .from('booking_services')
      .select('id, service_name')
      .eq('owner', vendorId);
    const services = (svcData as { id: number; service_name: string | null }[] | null) || [];
    const ids = services.map((s) => Number(s.id)).filter((n) => Number.isFinite(n) && n > 0);
    if (ids.length === 0) return EMPTY_VENDOR_RESERVATIONS;
    const nameById = new Map<number, string>();
    services.forEach((s) => nameById.set(Number(s.id), s.service_name || 'Service'));

    // 2) Contenus « service » du vendeur : prix par titre (nom normalisé).
    const { data: annonces } = await supabase
      .from('annonce')
      .select('titre, prix, type')
      .eq('idvendeur', vendorId);
    const priceByTitle = new Map<string, number>();
    ((annonces as { titre: string | null; prix: string | null; type: string | null }[] | null) || [])
      .filter((a) => (a.type || '').toLowerCase() === 'services')
      .forEach((a) => {
        const price = parseFloat(String(a.prix ?? '0'));
        if (!Number.isNaN(price) && price > 0) priceByTitle.set(normalizeKey(a.titre), price);
      });

    // 3) Réservations sur ces services.
    const { data: appts } = await supabase
      .from('booking_appointments')
      .select('id, service_id, fullname, date, time, status, created_at')
      .in('service_id', ids)
      .order('created_at', { ascending: false });
    const rows = (appts as {
      id: number;
      service_id: number | null;
      fullname: string | null;
      date: string | null;
      time: string | null;
      status: string | null;
      created_at: string | null;
    }[] | null) || [];

    let amount = 0;
    const result: VendorReservationStat[] = rows.map((a) => {
      const title = a.service_id ? (nameById.get(Number(a.service_id)) || 'Service') : 'Service';
      const price = priceByTitle.get(normalizeKey(title)) || 0;
      amount += price;
      return {
        id: Number(a.id),
        title,
        date: a.created_at || a.date || null,
        amount: price,
        status: a.status,
      };
    });
    return { count: result.length, amount, rows: result };
  } catch {
    return EMPTY_VENDOR_RESERVATIONS;
  }
}

/**
 * Charge toute l'activité récente du vendeur : commandes, réservations et
 * consultations IA, fusionnées dans un fil de notifications unique.
 */
export async function fetchVendorActivity(
  vendorId: number | null | undefined,
  limit = 14,
): Promise<VendorActivity> {
  if (!vendorId) return EMPTY_VENDOR_ACTIVITY;

  const notifications: VendorNotification[] = [];
  let orderCount = 0;
  let aiOrderCount = 0;
  let reservationCount = 0;
  let boutiqueViews = 0;

  // ── Commandes ──
  try {
    const { data } = await supabase
      .from('commande')
      .select('id, titre, nomclient, totalttc, monaie, statut_com_vendeur, date_time, noteclient, details')
      .eq('idvendeur', vendorId)
      .order('date_time', { ascending: false })
      .limit(60);
    const rows = (data as OrderRow[] | null) || [];
    orderCount = rows.length;
    rows.forEach((c) => {
      const viaAi = isViaAi(c.noteclient);
      if (viaAi) aiOrderCount += 1;
      const amount = parseFloat(String(c.totalttc ?? '0'));
      notifications.push({
        id: `order-${c.id}`,
        kind: 'order',
        title: viaAi ? 'Nouvelle commande via l\u2019assistant IA' : 'Nouvelle commande',
        detail: `${c.nomclient || 'Client'} · ${c.titre || `Commande #${c.id}`}${c.details ? ` — ${c.details}` : ''}`,
        amount: Number.isNaN(amount) ? null : amount,
        currency: c.monaie || null,
        date: c.date_time || '',
        icon: viaAi ? 'ri-robot-2-line' : 'ri-shopping-cart-2-line',
        tone: viaAi ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-900',
        viaAi,
      });
    });
  } catch {
    /* best-effort */
  }

  // ── Réservations ──
  try {
    const ids = await fetchVendorBookingServiceIds(vendorId);
    if (ids.length > 0) {
      const { data } = await supabase
        .from('booking_appointments')
        .select('id, service_id, fullname, date, time, status, created_at')
        .in('service_id', ids)
        .order('created_at', { ascending: false })
        .limit(60);
      const rows = (data as AppointmentRow[] | null) || [];
      reservationCount = rows.length;
      rows.forEach((a) => {
        const when = a.date
          ? `${new Date(a.date).toLocaleDateString('fr-FR')}${a.time ? ` à ${String(a.time).slice(0, 5)}` : ''}`
          : '';
        notifications.push({
          id: `res-${a.id}`,
          kind: 'reservation',
          title: 'Nouvelle réservation',
          detail: `${a.fullname || 'Client'}${when ? ` · ${when}` : ''}`,
          date: a.created_at || a.date || '',
          icon: 'ri-calendar-check-line',
          tone: 'bg-accent-100 text-accent-700',
        });
      });
    }
  } catch {
    /* best-effort */
  }

  // ── Consultations IA (contenus + boutique) ──
  try {
    const { data } = await supabase
      .from('ai_chat_product_events')
      .select('id, event_type, product_name, client_name, query, created_at')
      .eq('merchant_id', vendorId)
      .order('created_at', { ascending: false })
      .limit(60);
    const rows = (data as AiEventRow[] | null) || [];
    rows.forEach((e) => {
      const type = String(e.event_type || '');
      if (type === 'boutique') boutiqueViews += 1;
      const label =
        type === 'boutique'
          ? 'Votre boutique a été consultée via l\u2019IA'
          : type === 'cart'
          ? 'Un client a ajouté votre contenu au panier via l\u2019IA'
          : type === 'click'
          ? 'Un client a ouvert votre contenu via l\u2019IA'
          : 'Votre contenu est apparu dans l\u2019assistant IA';
      const who = e.client_name ? `${e.client_name} · ` : '';
      const what = e.product_name || e.query || 'Contenu';
      notifications.push({
        id: `ai-${e.id}`,
        kind: 'ai_view',
        title: label,
        detail: `${who}${what}`,
        date: e.created_at || '',
        icon: type === 'boutique' ? 'ri-store-2-line' : 'ri-robot-2-line',
        tone: 'bg-primary-100 text-primary-700',
      });
    });
  } catch {
    /* best-effort */
  }

  notifications.sort((a, b) => toTime(b.date) - toTime(a.date));

  return {
    orderCount,
    aiOrderCount,
    reservationCount,
    boutiqueViews,
    notifications: notifications.slice(0, limit),
  };
}